import { createContext, useContext } from 'react';
import { IService } from 'services/IService';
import { ST } from 'types';
import type { RunModel } from '../../models/RunModel/RunModel';
import { UserSN } from '../../models/User';
import {
    ExploreElementIn,
    ExploreElementOut,
    ExploreElementCreateIn,
} from '../../models/ExploreElementModel/ExploreElementModel';

export enum Area {
    test = 'test',
    live = 'live',
}
export type RunArea = 'testTricks' | 'prodTricks' | 'testRuns' | 'prodRuns';

export type ICloudStorage = {
    addRun: (trick: RunModel) => Promise<void>;
    updateRun: (trick: RunModel, area?: Area) => Promise<void>; // New method for updating tricks
    getRunsByUser: (uid: string, area?: Area) => Promise<Array<RunModel>>;
    getRunsById: (ids: Array<string>, area?: Area) => Promise<Array<RunModel>>;
    getRun: (id: string, area?: Area) => Promise<RunModel | null>;
    delRun: (id: string, area?: Area) => Promise<void>;
    getPublicRuns: (area: RunArea, newerThan: Date, operator: '<' | '>') => Promise<Array<RunModel>>; // New method for public tricks
    getOlderPublicRuns: (refDate?: Date, area?: Area) => Promise<Array<RunModel>>;
    getNewerPublicRuns: (refDate?: Date, area?: Area) => Promise<Array<RunModel>>;
    getFriendsRuns: (area?: Area) => Promise<Array<RunModel>>; // New method for friends tricks
    getUser: (uid: string) => Promise<UserSN | null>;
    getAllUsers: () => Promise<Array<UserSN>>;
    putUser: (user: UserSN, id?: string) => Promise<any>;

    downloadVideo: (url: string) => Promise<string>;
    uploadVideo: (
        deviceId: string,
        recordId: string,
        inBackground: boolean,
        area?: Area
    ) => Promise<string>;

    downloadPlay: (
        device_id_or_url: string,
        record_id?: string,
        area?: Area
    ) => Promise<{ data: ST.Play; uri: string }>;
    uploadRecord: (record: ST.Record, area?: Area) => Promise<string>;
    uploadPlay: (play: ST.Play, area?: Area) => Promise<string>;

    // ExploreElement methods
    addExploreElement: (element: ExploreElementCreateIn) => Promise<string>;
    getExploreElements: (area?: Area) => Promise<Array<ExploreElementOut>>;
    getExploreElementsByUser: (
        uid: string,
        area?: Area
    ) => Promise<Array<ExploreElementOut>>;
    getExploreElement: (
        id: string,
        area?: Area
    ) => Promise<ExploreElementOut | null>;
    updateExploreElement: (element: ExploreElementIn) => Promise<void>;
    deleteExploreElement: (id: string, area?: Area) => Promise<void>;

    // Profilbild-Upload mit optionalem Area-Parameter für explizite Bucket-Steuerung
    uploadProfilePhoto: (userId: string, file: File, area?: Area) => Promise<string>; // area steuert explizit den Storage-Bereich (Bucket); Logging in Implementierung beachten
} & IService;

const CloudStorageContext = createContext<null | ICloudStorage>(null);

export const StorageProvider = CloudStorageContext.Provider;
export function useCloudStorage() {
    const svc = useContext(CloudStorageContext);
    if (svc === null) {
        throw new Error(
            'CloudService cannot be null, please add a context provider'
        );
    }
    return svc;
}
